import { useEffect, useState } from 'react'

import { quantApi } from '@/app/lib/quant/quantApi'
import type { DataCoverage } from '@/app/lib/quant/types'

export type QuantFeedCoverageState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: DataCoverage }

/**
 * Spec 040: the stored-data coverage read for the feed panel. One read per
 * open (`openSeq` changes on every open), aborted on close. Independent of
 * `useQuantData`, which the Config preview owns.
 */
export function useQuantFeedCoverage(open: boolean, openSeq: number): QuantFeedCoverageState {
  const [held, setHeld] = useState<{ seq: number; result: 'error' | DataCoverage } | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const controller = new AbortController()
    quantApi.getData(controller.signal).then(
      (data) => {
        if (!cancelled) setHeld({ seq: openSeq, result: data })
      },
      () => {
        if (!cancelled) setHeld({ seq: openSeq, result: 'error' })
      },
    )
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [open, openSeq])

  if (!open) return { status: 'idle' }
  if (held === null || held.seq !== openSeq) return { status: 'loading' }
  if (held.result === 'error') return { status: 'error' }
  return { status: 'ready', data: held.result }
}
